import DottedTexture from '@/assets/branding/dotted-texture.svg?react';
import TicketsIcon from '@/assets/icons/tickets.svg';
import { Dialog, DialogContent } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { useCapitalPlans } from '@/features/vest-capital/api/get-capital-plans';
import LiquidationTable from '@/features/vest-capital/components/liquidation-table';
import useFundedAccount from '@/hooks/use-funded-account';
import { privateApi } from '@/lib/api-clients/rest-client';
import { getCapitalStatus } from '@/lib/api/get-capital-status';
import { logConsole } from '@/lib/utils';
import { useAccountStore } from '@/store/use-account-store';
import { useCapitalStore } from '@/store/use-capital-store';
import { useModalStore } from '@/store/use-modal-store';
import { CapitalStatus, ErrorCode, ErrorMsgs, getErrorMsg } from '@/types';
import { cn } from '@/utils/cn';
import { centsToDollars, formatNumber } from '@/utils/value-format';
import {
  getAccessToken,
  useCreateWallet,
  usePrivy,
  WalletWithMetadata,
} from '@privy-io/react-auth';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

const COMPENSATION_MESSAGE = `Funded Account Compensation Request
(Write the details here...)`;

export default function CapitalResultModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { showNewMessage } = useIntercom();
  const discountCount = useCapitalStore((state) => state.discountGroups.length);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isOpen = useModalStore((state) => state.isCapitalResultModalOpen);

  const { address, capitalStatus, capitalStatusByAddress } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      capitalStatus: state.getCapitalStatus(state.address),
      capitalStatusByAddress: state.capitalStatusByAddress,
    })),
  );

  const { data: capitalPlans } = useCapitalPlans();
  const { capitalStatusData } = useFundedAccount();
  const { createWallet } = useCreateWallet();

  const isGoalPassed = capitalStatus === CapitalStatus.GOAL_PASSED;
  const isBlocked =
    capitalStatus === CapitalStatus.BLOCKED ||
    capitalStatus === CapitalStatus.FUNDED_BLOCKED;
  const wasFunded =
    capitalStatus === CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS ||
    capitalStatus === CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN;

  const accountSize = useMemo(() => {
    if (!address || !capitalStatusData) {
      return 0;
    }

    const plan = capitalPlans?.find(
      (plan) =>
        plan.name ===
        capitalStatusData[address.toLowerCase()]?.active_capital_plan,
    );

    return centsToDollars(plan?.metadata?.account_size ?? '0');
  }, [address, capitalPlans, capitalStatusData]);

  if (isBlocked) return;

  async function claim() {
    if (!isGoalPassed || !address) {
      return;
    }

    setIsLoading(true);

    let fundedAddress: string;
    // NOTE: check if there's a free wallet first
    const freeWalletAddress = (
      user?.linkedAccounts.find(
        (acc) =>
          acc.type === 'wallet' &&
          capitalStatusByAddress[acc.address.toLowerCase()] ===
            CapitalStatus.PRE_FUNDED,
      ) as WalletWithMetadata
    )?.address;

    const { setAddress, setCapitalStatus } = useAccountStore.getState();

    try {
      if (!freeWalletAddress) {
        fundedAddress = (await createWallet({ createAdditional: true }))
          .address;
        // NOTE: immediately update new wallet to PRE_FUNDED to avoid creating multiple wallets
        // this status will persist in storage, multiple wallets could only be created by claiming on different device after failure from BE
        setCapitalStatus(CapitalStatus.PRE_FUNDED, fundedAddress);
      } else {
        fundedAddress = freeWalletAddress;
      }

      const accessToken = await getAccessToken();
      await privateApi.post('/capital/activateFunded', {
        funded_register_params: {
          primaryAddr: fundedAddress.toLowerCase(),
          privyAccessToken: accessToken,
          isFunded: true,
        },
      });

      logConsole(false, '[Funded Claim]: Awaiting status change');

      // NOTE: wait for status update from ws to not get 'created' on new funded
      await new Promise<void>((res, rej) => {
        let timeout: number;
        let unsubscribe: () => void;

        const cleanup = () => {
          unsubscribe();
          window.clearTimeout(timeout);
        };

        unsubscribe = useAccountStore.subscribe((state) => {
          const status = state.getCapitalStatus(address);
          if (status !== CapitalStatus.FUNDED_CLAIMED) return;
          cleanup();
          res();
        });

        timeout = window.setTimeout(() => {
          getCapitalStatus({ addresses: [address] })
            .then((response) => {
              if (
                response[address].capital_status ===
                CapitalStatus.FUNDED_CLAIMED
              ) {
                res();
              } else {
                rej(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
              }
            })
            .catch((_err) => rej(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT))
            .finally(cleanup);
        }, 15_000);
      });

      logConsole(
        false,
        "[Funded Claim]: Status changed to 'claimed', switching account to funded",
      );

      setCapitalStatus(CapitalStatus.FUNDED, fundedAddress);

      useModalStore.setState({ isCapitalResultModalOpen: false });
      setAddress(fundedAddress);
      navigate(ROUTES.TRADE);
    } catch (err) {
      if (typeof err === 'number' && err in ErrorMsgs) {
        toast.error(getErrorMsg(err, t));
      } else {
        const error =
          (err as AxiosError<{ detail: { msg: string } }>).response?.data
            ?.detail.msg ??
          (err as Error).message ??
          'Unknown error';
        console.error('[Funded Account Claim] Error occurred:', error);
        toast.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          useModalStore.setState({ isCapitalResultModalOpen: false });
        }
      }}
    >
      <DialogContent
        hideCloseButton
        className="no-scrollbar max-h-full gap-10 overflow-hidden overflow-y-auto border-none sm:max-w-[440px] sm:!rounded-3xl"
      >
        <div className="relative flex flex-col gap-5 p-5 pb-0 pt-8 sm:p-8 sm:pb-0">
          <DottedTexture
            className={cn(
              'pointer-events-none absolute top-0 z-[-1] h-auto w-full scale-[1.7]',
              !isGoalPassed ? '[&_ellipse]:fill-[#3B66FC]' : undefined,
            )}
          />

          <div className="text-center text-4xl font-semibold capitalize leading-[130%]">
            {isGoalPassed
              ? t('capitalResultModal.congratulations')
              : t('capitalResultModal.fortuneFavorsTheBold')}
          </div>

          {isGoalPassed ? (
            <div className="text-center text-xl text-muted-foreground">
              {t('capitalResultModal.youPassedExamination')}
              <br />
              {formatNumber(accountSize, { style: 'currency' })}{' '}
              {t('capitalResultModal.willBeSentToAccount')}.
            </div>
          ) : (
            <>
              <div className="text-center text-xl text-muted-foreground">
                {t('capitalResultModal.justNotThisTime')}
                <br />
                {t('capitalResultModal.theMarketDidntPlayAlong')}
                <br />
                <br />
                {(() => {
                  switch (capitalStatus) {
                    case CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS:
                    case CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS:
                      return t('capitalResultModal.maxDailyLossLimit');
                    case CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN:
                    case CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN:
                      return t('capitalResultModal.maxDrawdownLimit');
                  }
                })()}{' '}
                {t('capitalResultModal.wasExceeded')},{' '}
                {wasFunded
                  ? t('capitalResultModal.fundedAccountHasBeenLiquidated')
                  : t('capitalResultModal.failingEvaluation')}
                .<br />
                {t('capitalResultModal.tryAgain')}?
              </div>

              <LiquidationTable />
            </>
          )}
        </div>
        <div className="flex flex-col gap-3 p-5 pt-0 sm:p-8 sm:pt-0">
          {isGoalPassed ? (
            <Button
              variant="contrast"
              size="lg"
              loading={isLoading}
              onClick={claim}
            >
              {t('capitalResultModal.goToTrade')}
            </Button>
          ) : (
            <>
              <Button
                variant="foreground"
                size="lg"
                onClick={() => {
                  useModalStore.setState({ isCapitalResultModalOpen: false });
                  if (discountCount > 0) {
                    useModalStore.setState({ isDiscountsModalOpen: true });
                  }
                  navigate(`${ROUTES.VEST_CAPITAL}#capital-plans`);
                }}
              >
                {t('capitalResultModal.tryAgain')}
                {discountCount > 0 && (
                  <span className="flex items-center gap-1 rounded-md bg-muted-foreground/30 py-1 pl-2 pr-3">
                    <img
                      src={TicketsIcon}
                      alt="Tickets"
                      className="h-auto w-6 max-w-none"
                    />
                    <span className="font-medium">{discountCount}</span>
                  </span>
                )}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => {
                  useModalStore.setState({ isCapitalResultModalOpen: false });
                  navigate(
                    `${ROUTES.PORTFOLIO}?view=order-history#portfolio-tables`,
                  );
                }}
              >
                {t('capitalResultModal.checkHistory')}
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => showNewMessage(COMPENSATION_MESSAGE)}
              >
                {t('capitalResultModal.requestCompensation')}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
